import React from "react";
import { Link } from "react-router-dom";

function SectionContent({ section }) {
  return (
    <div className="space-y-4 text-base leading-relaxed text-[#0F172A]/90">
      {section.paragraphs?.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
      {section.lists?.map((list, listIndex) => (
        <div key={listIndex} className="space-y-2">
          {list.title && <h3 className="text-lg font-semibold text-[#0054c9]">{list.title}</h3>}
          <ul className="list-disc list-inside space-y-2">
            {list.items.map((item, itemIndex) => (
              <li key={itemIndex}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
      {section.links?.length > 0 && (
        <div className="flex flex-wrap gap-3 pt-2">
          {section.links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="inline-flex items-center rounded-full border border-[#0054c9]/30 bg-[#E8F2F4] px-4 py-2 text-sm font-semibold text-[#0054c9] hover:bg-[#0054c9] hover:text-white transition"
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InstitutionalPage({
  eyebrow,
  title,
  subtitle,
  badges = [],
  sections = [],
  sidebar,
  cta,
  relatedLinks = [],
}) {
  return (
    <div className="bg-[#E8F2F4] text-[#0F172A] min-h-screen">
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0054c9] via-[#023680] to-[#010e2f] text-white">
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 sm:py-20">
          {eyebrow && (
            <p className="uppercase tracking-[0.35em] text-xs font-semibold mb-4 text-[#D7E3FF]">
              {eyebrow}
            </p>
          )}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-5 leading-tight max-w-4xl">
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg md:text-xl max-w-3xl text-[#E8EEFF] leading-relaxed">{subtitle}</p>
          )}
          {badges.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-3 text-sm text-[#E8EEFF]/90">
              {badges.map((badge) => (
                <span
                  key={badge}
                  className="bg-white/10 border border-white/20 px-4 py-2 rounded-full"
                >
                  {badge}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 sm:py-16 grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-8 lg:gap-10">
        {(sections.length > 0 || sidebar) && (
          <div className="order-2 lg:order-1 space-y-6">
            {sections.length > 0 && (
              <nav className="bg-white/90 backdrop-blur border border-[#C7D4E3] rounded-2xl p-5 shadow-sm lg:sticky lg:top-28">
                <h2 className="text-base font-semibold text-[#003B8E] mb-3">En esta página</h2>
                <ul className="space-y-2 text-sm font-medium">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        className="text-[#0F172A]/80 hover:text-[#0054c9] transition"
                      >
                        {section.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
            {sidebar && (
              <div className="bg-white border border-[#C7D4E3] rounded-2xl p-5 shadow-sm">
                <h2 className="text-base font-semibold text-[#003B8E] mb-3">{sidebar.title}</h2>
                <div className="text-sm leading-relaxed text-[#0F172A]/80 space-y-1">
                  {sidebar.lines?.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                  {sidebar.text && <p>{sidebar.text}</p>}
                </div>
              </div>
            )}
            {relatedLinks.length > 0 && (
              <div className="bg-white border border-[#C7D4E3] rounded-2xl p-5 shadow-sm">
                <h2 className="text-base font-semibold text-[#003B8E] mb-3">Enlaces relacionados</h2>
                <ul className="space-y-2 text-sm">
                  {relatedLinks.map((link) => (
                    <li key={link.path}>
                      <Link to={link.path} className="text-[#0054c9] font-medium hover:underline">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="space-y-8 order-1 lg:order-2">
          {sections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className="bg-white border border-[#C7D4E3] rounded-3xl p-6 sm:p-8 shadow-sm scroll-mt-28"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-[#003B8E] mb-4">{section.title}</h2>
              <SectionContent section={section} />
            </section>
          ))}

          {cta && (
            <section className="bg-[#003B8E] text-white rounded-3xl p-6 sm:p-8 shadow-lg">
              <h2 className="text-xl sm:text-2xl font-bold mb-3">{cta.title}</h2>
              <p className="text-base leading-relaxed text-[#E8EEFF]">{cta.text}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
